import { Injectable, Type } from '@angular/core';
import { GungSettingsBaseViewComponent } from '../components/gung-settings-base-view/gung-settings-base-view.component';
import { first, forkJoin, map, Observable, of } from 'rxjs';
import { GungSettingsUsersViewComponent } from '../components/gung-settings/gung-settings-users-view/gung-settings-users-view.component';
import { ConfigurationsService } from './configurations.service';
import { MyCompanyComponent } from '../components/my-company/my-company.component';
import { CustomerBrandingComponent } from '../components/customer-branding/customer-branding.component';
import { GungSettingsSecurityViewComponent } from '../components/gung-settings/gung-settings-security-view/gung-settings-security-view.component';
import { GungSettingsCustomerTemplatesComponent } from '../components/gung-settings/gung-settings-customer-templates/gung-settings-customer-templates.component';
import { GungSettingsConfigOrderViewComponent } from '../components/gung-settings/gung-settings-config-order-view/gung-settings-config-order-view.component';
import { GungSettingsConfigOfferViewComponent } from '../components/gung-settings/gung-settings-config-offer-view/gung-settings-config-offer-view.component';
import { GungSettingsNavigationViewComponent } from '../components/gung-settings/gung-settings-general-view/gung-settings-navigation-view.component';
import { GungSettingsApiComponent } from '../components/gung-settings/gung-settings-api/gung-settings-api.component';
import { GungSettingsApiUsersComponent } from '../components/gung-settings/gung-settings-api-users/gung-settings-api-users.component';
import { FeatureActivatedGuardWrapper } from '../guards/feature-activated/feature-activated.guard';
import { GungSettingsSepaDirectDebitComponent } from '../components/gung-settings/gung-settings-sepa-direct-debit/gung-settings-sepa-direct-debit.component';
import { GungSettingsZendrSenderConfigComponent } from '../components/gung-settings/gung-settings-zendr-sender-config/gung-settings-zendr-sender-config.component';
import { GungSettingsPunchoutComponent } from '../components/gung-settings/gung-settings-punchout/gung-settings-punchout.component';
import { GungSettingsDebugProductViewComponent } from '../components/gung-settings/gung-settings-debug-product-view/gung-settings-debug-product-view.component';


export interface GungSettingsMenuItem {
  id: string;
  translationLabel: string;
  componentView: Type<GungSettingsBaseViewComponent> | undefined;
  prependIcon?: string;
  appendIcon?: string;
  isSeparator?: boolean;
  disabled?: boolean;
  hide?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class GungSettingsConfigService {


  constructor(
    protected configurationsService: ConfigurationsService,
    protected backendFeatureGuard: FeatureActivatedGuardWrapper
  ) {
  }

  public getGungSettingsMenu(): Observable<GungSettingsMenuItem[]> {
    const menu: GungSettingsMenuItem[] = [];

    menu.push({
      id: 'navigation',
      translationLabel: 'NAVIGATION',
      prependIcon: 'fa-light fa-compass',
      componentView: GungSettingsNavigationViewComponent
    });

    menu.push({
      id: 'my-company',
      translationLabel: 'MY_COMPANY',
      prependIcon: 'fa-light fa-building',
      componentView: MyCompanyComponent
    });

    menu.push({
      id: 'users',
      translationLabel: 'USERS',
      prependIcon: 'fa-light fa-user',
      componentView: GungSettingsUsersViewComponent
    });

    menu.push({
      id: 'security',
      translationLabel: 'SECURITY',
      prependIcon: 'fa-light fa-lock',
      componentView: GungSettingsSecurityViewComponent
    });

    menu.push({
      id: 'customer-templates',
      translationLabel: 'CUSTOMER_TEMPLATES',
      prependIcon: 'fa-light fa-users',
      componentView: GungSettingsCustomerTemplatesComponent
    });

    menu.push({
      id: 'orders',
      translationLabel: 'ORDERS',
      prependIcon: 'fa-solid fa-briefcase',
      componentView: GungSettingsConfigOrderViewComponent
    });

    menu.push({
      id: 'offers',
      translationLabel: 'OFFERS',
      prependIcon: 'fa-light fa-briefcase',
      componentView: GungSettingsConfigOfferViewComponent
    });

    return forkJoin({
      menu: of(menu),
      apiActivated: this.backendFeatureGuard.isActivated('gung-api', '2.0.0', true).pipe(first()),
      sepaDirectDebitActivated: this.backendFeatureGuard.isActivated('sepa-direct-debit', '1.0.0', true).pipe(first()),
      zendrActivated: this.backendFeatureGuard.isActivated('zendr', '1.0.0', true).pipe(first()),
      punchoutActivated: this.backendFeatureGuard.isActivated('gung-punchout', '1.0.0', true).pipe(first()),
      customerBrandingActivated: this.backendFeatureGuard.isActivated('customer-branding', '0.0.0', true).pipe(first())
    }).pipe(map(({ menu, apiActivated, sepaDirectDebitActivated, zendrActivated, punchoutActivated, customerBrandingActivated })=>{
      if(apiActivated){
        menu.push({
          id: 'api-settings',
          translationLabel: 'API_SETTINGS',
          prependIcon: 'fa-solid fa-gears',
          componentView: GungSettingsApiComponent
        });

        menu.push({
          id: 'api-users',
          translationLabel: 'API_USERS',
          prependIcon: 'fa-solid fa-users-gear',
          componentView: GungSettingsApiUsersComponent
        });
      }

      if (sepaDirectDebitActivated) {
        menu.push({
          id: 'sepa-direct-debit',
          translationLabel: 'SEPA_DIRECT_DEBIT',
          prependIcon: 'fa-solid fa-credit-card',
          componentView: GungSettingsSepaDirectDebitComponent
        });
      }

      if (zendrActivated) {
        menu.push({
          id: 'zendr-sender-config',
          translationLabel: 'ZENDR_SENDER_CONFIG',
          prependIcon: 'fa-solid fa-address-card',
          componentView: GungSettingsZendrSenderConfigComponent
        });
      }

      if (punchoutActivated) {
        menu.push({
          id: 'punchout',
          translationLabel: 'PUNCHOUT',
          prependIcon: 'fa-solid fa-handshake',
          componentView: GungSettingsPunchoutComponent,
          disabled: false
        });
      }
      if (customerBrandingActivated) {
        menu.push({
          id: 'customer-branding',
          translationLabel: 'CUSTOMER_BRANDING',
          prependIcon: 'fa-light fa-palette',
          componentView: CustomerBrandingComponent
        });
      }

      menu.push({
        id: 'debug-troubleshoot',
        translationLabel: 'DEBUG_TROUBLESHOOT',
        prependIcon: 'fa-solid fa-bug',
        componentView: GungSettingsDebugProductViewComponent
      });

      return menu;
    }))

  }
}
